import React from "react";
import PropTypes from "prop-types";

import FormInput from "./FormInput";
import FormTagsInput from "./FormTagsInput";
import FormSelect from "./FormSelect";
import FormDatePicker from "./ReactFlatpickr";
import FormTextarea from "./FormTextarea";
import FormNumberInput from "./FormNumberInput";
import FormInputPassword from "./FormInputPassword";
import FormPhoneInput from "./FormPhoneInput";
import FormCheckbox from "./FormCheckbox";
import FormRadio from "./FormRadio";
import FormInputFile from "./FormInputFile";
import FormMultipleInputFile from "./FormMultipleInputFile";
import Switch from "./Switch";

import { getSelectOptions } from "utils/optionHelper";

const FormRenderer = ({
  fields = [],
  values = {},
  errors = {},
  handleChange,
  optionConfig = {},
  switchConfig = {
    ValueOne: "1",
    ValueTwo: "0",
    LabelOne: "Active",
    LabelTwo: "Inactive",
  },
  isrow = true
}) => {

  const renderField = (field) => {
    if (!field || !field.name) return null;

    const commonProps = {
      field,
      value: values?.[field.name] ?? "",
      handleChange,
      error: errors?.[field.name] ?? null,
      disabled: field.disabled || false,
      ErrIsScroll: field.ErrIsScroll || false
    };

    const fileProps = {
      ...commonProps,
      imagePreview:
        values?.[`${field.name}_preview`] ?? values?.[field.name] ?? null,
    };

    switch (field.type) {
      case "select":
        return (
          <FormSelect
            key={field.id || field.name}
            {...commonProps}
            options={getSelectOptions(field.name, optionConfig)}
          />
        );

      case "textarea":
        return <FormTextarea key={field.id || field.name} {...commonProps} />;

      case "date":
      case "datetime":
      case "monthYear":
        return (
          <FormDatePicker
            {...commonProps}
            key={field.id || field.name}
            mode={field.type}
            selectMode={field.selectMode || "single"}
            disablePast={field.disablePast}
            disablePastMonth={field.disablePastMonth}
            yearLimit={field.yearLimit}
          />
        );

      case "number":
        return <FormNumberInput
          key={field.id || field.name}
          {...commonProps} />;

      case "password":
        return <FormInputPassword
          key={field.id || field.name}
          {...commonProps} />;

      case "phone":
        return (
          <FormPhoneInput
            key={field.id || field.name}
            {...commonProps}
            country_code={values?.[field.country_code_field || "country_dial_code"]}
            country_code_field={field.country_code_field}
          />
        );

      case "checkbox":
        return <FormCheckbox
          key={field.id || field.name}
          {...commonProps} />;

      case "radio":
        return <FormRadio
          key={field.id || field.name}
          {...commonProps} />;

      case "file":
        return field.multiple ? (
          <FormMultipleInputFile
            key={field.id || field.name}
            {...fileProps} />
        ) : (
          <FormInputFile
            key={field.id || field.name}
            {...fileProps} />
        );

      case "multi-file":
        return <FormMultipleInputFile
          key={field.id || field.name}
          {...fileProps} />;

      case "tags":
        return <FormTagsInput
          key={field.id || field.name}
          {...commonProps} />;

      case "switch":
        return <Switch
          key={field.id || field.name}
          {...commonProps}
          values={values?.[field.name] ?? ""}
          {...(field.switchConfig || switchConfig)}
        />;

      default:
        return <FormInput
          key={field.id || field.name}
          {...commonProps} />;
    }
  };

  return isrow ? (
    <div className="row g-3">
      {fields.map((field) => renderField(field))}
    </div>
  ) : (
    fields.map((field) => renderField(field))
  );
};

FormRenderer.propTypes = {
  fields: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  optionConfig: PropTypes.object,
};

export default React.memo(FormRenderer);
